import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Row, Col, TimePicker, Divider, message, Spin } from 'antd';
import { fetchLocationByPincode, validateIndianPhoneNumber, INDIAN_STATES, generateIndianBranchCode, getBusinessHoursSuggestion } from '../utils/indiaHelpers';
import { fetchExistingBranchCodes } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { BRANCH_TYPE_OPTIONS } from '../constants/branchConstants';

const { Option } = Select;

const BranchFormFields = ({ form, users, usersLoading }) => {
  const { canViewField, canEditField } = usePermissions();

  // name
  const canFieldNameShow = canEditField('branches', 'name') || canViewField('branches', 'name');
  const canFieldNameEdit = canEditField('branches', 'name');
  // code
  const canFieldCodeShow = canEditField('branches', 'code') || canViewField('branches', 'code');
  const canFieldCodeEdit = canEditField('branches', 'code');
  // address
  const canFieldAddressShow = canEditField('branches', 'address') || canViewField('branches', 'address');
  const canFieldAddressEdit = canEditField('branches', 'address');
  // contactDetails
  const canFieldContactShow = canEditField('branches', 'contactDetails') || canViewField('branches', 'contactDetails');
  const canFieldContactEdit = canEditField('branches', 'contactDetails');
  // manager
  const canFieldManagerShow = canEditField('branches', 'manager') || canViewField('branches', 'manager');
  const canFieldManagerEdit = canEditField('branches', 'manager');
  // operationalHours
  const canFieldOpHoursShow = canEditField('branches', 'operationalHours') || canViewField('branches', 'operationalHours');
  const canFieldOpHoursEdit = canEditField('branches', 'operationalHours');
  // status
  const canFieldStatusShow = canEditField('branches', 'status') || canViewField('branches', 'status');
  const canFieldStatusEdit = canEditField('branches', 'status');
  // branchType
  const canFieldBranchTypeShow = canEditField('branches', 'branchType') || canViewField('branches', 'branchType');
  const canFieldBranchTypeEdit = canEditField('branches', 'branchType');

  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [existingCodes, setExistingCodes] = useState([]);
  
  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Under Maintenance', value: 'under_maintenance' },
    { label: 'Closed', value: 'closed' },
  ];

  const workingDaysOptions = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  // Fetch existing branch codes on component mount
  useEffect(() => {
    const loadExistingCodes = async () => {
      try {
        const response = await fetchExistingBranchCodes();
        setExistingCodes(response.data || []);
      } catch (error) {
        console.error('Error loading existing branch codes:', error);
      }
    };
    
    loadExistingCodes();
  }, []);

  // Handle pincode change and auto-fill location details
  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    
    if (pincode && pincode.length === 6) {
      setPincodeLoading(true);
      try {
        const locationData = await fetchLocationByPincode(pincode);
        
        if (locationData) {
          // Update form fields with fetched data
          form.setFieldsValue({
            'address.city': locationData.city,
            'address.state': locationData.state,
            'address.country': locationData.country,
            'address.region': locationData.region,
            'address.district': locationData.district,
            'address.division': locationData.division,
            'address.circle': locationData.circle
          });
          
          message.success('Location details auto-filled from pincode');
        } else {
          message.warning('No location data found for this pincode');
        }
      } catch (error) {
        console.error('Error auto-filling location:', error);
        message.error('Failed to fetch location details');
      } finally {
        setPincodeLoading(false);
      }
    }
  };  // Handle branch name change to generate code
  const handleNameChange = (e) => {
    const name = e.target.value;
    const city = form.getFieldValue(['address', 'city']);
    const state = form.getFieldValue(['address', 'state']);
    
    if (name && city && state && !form.getFieldValue('code')) {
      const suggestedCode = generateIndianBranchCode(city, state, existingCodes);
      form.setFieldsValue({ code: suggestedCode });
    }
  };

  // Custom phone number validator
  const validatePhoneNumber = (_, value) => {
    if (value && !validateIndianPhoneNumber(value)) {
      return Promise.reject(new Error('Please enter a valid Indian phone number (10 digits starting with 6-9)'));
    }
    return Promise.resolve();
  };

  return (
    <>
      {/* Basic Information */}
      <Row gutter={[16, 0]}>
        {canFieldNameShow && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label="Branch Name"
              rules={[
                { required: true, message: 'Please enter branch name' },
                { min: 2, message: 'Branch name must be at least 2 characters' }
              ]}
            >
              <Input 
                placeholder="Enter branch name" 
                onChange={handleNameChange}
                disabled={!canFieldNameEdit}
              />
            </Form.Item>
          </Col>
        )}
        {canFieldCodeShow && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="code"
              label="Branch Code"
              rules={[
                { required: true, message: 'Please enter branch code' },
                { pattern: /^[A-Z0-9]+$/, message: 'Code should contain only uppercase letters and numbers' }
              ]}
              extra="Auto-generated based on city and state"
            >
              <Input placeholder="e.g., CHNDL01, MUPMH02" style={{ textTransform: 'uppercase' }} disabled={!canFieldCodeEdit} />
            </Form.Item>
          </Col>
        )}
      </Row>

      {canFieldAddressShow && (
        <>
          <Divider orientation="left" orientationMargin="0">Address Information</Divider>

          {/* Address Information */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="address.postalCode" 
                label="PIN Code"
                rules={[
                  { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit PIN code' }
                ]}
                extra="Enter PIN code to auto-fill city, state, and country"
              >
                <Input 
                  placeholder="e.g., 110001"
                  onChange={handlePincodeChange}
                  suffix={pincodeLoading ? <Spin size="small" /> : null}
                  maxLength={6}
                  disabled={!canFieldAddressEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="address.city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="Enter city" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="address.state"
                label="State"
                rules={[{ required: true, message: 'Please enter state' }]}
              >
                <Select
                  placeholder="Select or type state"
                  showSearch
                  allowClear
                  disabled={!canFieldAddressEdit}
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? '').includes(input.toLowerCase())
                  }
                >
                  {INDIAN_STATES.map(state => (
                    <Option key={state} value={state}>
                      {state}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="address.country" 
                label="Country"
                initialValue="India"
              >
                <Input placeholder="Enter country" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="address.region" label="Region">
                <Input placeholder="e.g., South Bengal" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="address.district" label="District">
                <Input placeholder="e.g., Bardhaman" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="address.division" label="Division">
                <Input placeholder="e.g., Asansol" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="address.circle" label="Circle">
                <Input placeholder="e.g., West Bengal" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="address.street" label="Street Address">
                <Input placeholder="Enter complete street address" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="address.landmark" label="Landmark">
                <Input placeholder="Enter nearby landmark (optional)" disabled={!canFieldAddressEdit} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {canFieldContactShow && (
        <>
          <Divider orientation="left" orientationMargin="0">Contact Information</Divider>

          {/* Contact Information */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="contactDetails.phone"
                label="Phone Number"
                rules={[
                  { validator: validatePhoneNumber }
                ]}
                extra="Indian mobile number (10 digits starting with 6-9)"
              >
                <Input 
                  placeholder="e.g., 9876543210 or +91 98765 43210"
                  addonBefore="+91"
                  disabled={!canFieldContactEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="contactDetails.email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input placeholder="branch@company.com" disabled={!canFieldContactEdit} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="contactDetails.fax" label="Fax Number (Optional)">
                <Input placeholder="Enter fax number if available" disabled={!canFieldContactEdit} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Divider orientation="left" orientationMargin="0">Management Information</Divider>

      {/* Management Information */}
      <Row gutter={[16, 0]}>
        {canFieldManagerShow && (
          <Col xs={24} sm={12}>
            <Form.Item name="manager" label="Branch Manager">
              <Select
                placeholder="Select branch manager"
                loading={usersLoading}
                allowClear
                showSearch
                disabled={!canFieldManagerEdit}
                filterOption={(input, option) =>
                  (option?.children?.toLowerCase() ?? '').includes(input.toLowerCase())
                }
              >
                {users.map(user => (
                  <Option key={user._id} value={user._id}>
                    {user.name || user.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {canFieldStatusShow && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status" disabled={!canFieldStatusEdit}>
                {statusOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {canFieldBranchTypeShow && (
          <Col xs={24} sm={12}>
            <Form.Item
              name="branchType"
              label="Branch Type"
              initialValue="BRANCH"
              rules={[{ required: true, message: 'Please select branch type' }]}
              extra="HO = Head Office (manages bank funds). Branch = regular branch (cash only)."
            >
              <Select placeholder="Select branch type" disabled={!canFieldBranchTypeEdit}>
                {BRANCH_TYPE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      {canFieldOpHoursShow && (
        <>
          <Divider orientation="left" orientationMargin="0">Operational Hours</Divider>

          {/* Operational Hours */}
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name="operationalHours.openTime" label="Opening Time">
                <TimePicker 
                  format="HH:mm" 
                  placeholder="Select opening time"
                  style={{ width: '100%' }}
                  disabled={!canFieldOpHoursEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="operationalHours.closeTime" label="Closing Time">
                <TimePicker 
                  format="HH:mm" 
                  placeholder="Select closing time"
                  style={{ width: '100%' }}
                  disabled={!canFieldOpHoursEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="operationalHours.workingDays" label="Working Days">
                <Select
                  mode="multiple"
                  placeholder="Select working days"
                  allowClear
                  disabled={!canFieldOpHoursEdit}
                >
                  {workingDaysOptions.map(day => (
                    <Option key={day} value={day}>
                      {day}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default BranchFormFields;
